import { computeSetupChecklistItems, SetupChecklistItems } from '@griply/utils';
import { addDays } from 'date-fns';
import { useExternalCalendarConnections } from 'features/external-connections';
import { useGoals } from 'features/goals';
import {
  UPCOMING_WINDOW_DAYS,
  useActiveTasks,
  useAllCompletedTasks,
  useHabits,
  useTodaysCompletedHabits,
  useUpcomingTenDaysTasks,
} from 'features/tasks';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { getScheduleByDate, isScheduleDue } from 'shared/utils/schedules';

export const useSetupChecklistItems = (): SetupChecklistItems => {
  const user = useUser();
  const goals = useGoals();
  const habits = useHabits();
  const completedTasks = useAllCompletedTasks();
  const completedHabitsToday = useTodaysCompletedHabits();
  const activeTasks = useActiveTasks();
  const upcomingTasks = useUpcomingTenDaysTasks();
  const connections = useExternalCalendarConnections();
  const today = useToday();

  return useMemo(() => {
    const weekStartsOn = getWeekStartsOn(user);

    const hasFutureHabit = Array.from(
      { length: UPCOMING_WINDOW_DAYS },
      (_, index) => addDays(today, index + 1),
    ).some((day) =>
      habits.some((habit) => {
        const schedule = getScheduleByDate(habit.schedules, {
          date: day,
          today,
        });
        return (
          !!schedule && isScheduleDue(schedule, { today: day, weekStartsOn })
        );
      }),
    );

    return computeSetupChecklistItems({
      goalCount: goals.length,
      habitCount: habits.length,
      hasCompletedTaskOrHabit:
        !!completedTasks.length || !!completedHabitsToday.length,
      hasAnyTask: !!activeTasks.length || !!completedTasks.length,
      hasFutureTask: !!upcomingTasks.length || hasFutureHabit,
      hasCalendarConnection: !!connections.length,
    });
  }, [
    user,
    goals,
    habits,
    completedTasks,
    completedHabitsToday,
    activeTasks,
    upcomingTasks,
    connections,
    today,
  ]);
};
